import { useState } from "react";
import { useDismissToast } from "../../common/hooks/useDismissToast";
import { DataTestids } from "../../common/miscellaneous/DataTestids";
import { buildBeginLoading } from "../miscellaneous/buildBeginLoading";
import { buildDismissErrors } from "../miscellaneous/buildDismissErrors";
import { FinishLoading } from "../miscellaneous/finishWithStatus";

/**
 * Return this type from async function to fulfill loading contract.
 */
export type FinishReturn = Promise<
  ReturnType<ReturnType<ReturnType<typeof useAsyncLoader>["beginLoading"]>>
>;

export type FinishData<T> = { readonly tag: "data"; readonly data: T };
export type FinishErrors = {
  readonly tag: "errors";
  readonly errors: readonly string[];
  readonly uuid: string;
};
export type FinishLoadingInput<T> = FinishData<T> | FinishErrors;

export type State = (
  | { readonly tag: "initial" }
  | { readonly tag: "loading" }
  | { readonly tag: "success" }
  | FinishErrors
  | { readonly tag: "errors-dismissed"; readonly errors: readonly string[] }
) & { readonly key: DataTestids };

export type BeginLoadingInput = {
  readonly errorsCallback?: (errors: readonly string[]) => null;
  readonly finishCallback?: () => null;
};

// just use `null` for type `T` if you store your data elsewhere, and don't want to enforce data upon success
export type AsyncLoader<T> = {
  readonly beginLoading: (input?: BeginLoadingInput) => FinishLoading<T>;
  readonly data: T | "initial";
  readonly dismissErrors: () => "state-set" | "no-op";
} & State;

export function useAsyncLoader<T = null>(key: DataTestids): AsyncLoader<T> {
  const [loading, setLoading] = useState<State>({ tag: "initial", key });
  const [data, setData] = useState<T | "initial">("initial");
  const dismissToast = useDismissToast();

  return {
    ...loading,
    data,
    beginLoading: buildBeginLoading<T>(setLoading, setData, key, dismissToast),
    dismissErrors: buildDismissErrors(loading, setLoading, key),
  };
}
